<template>
  <div class="marcas-contenedor">
    <h2 class="mb-4">Marcas de Productos</h2>

    <div class="mb-3">
      <button v-if="puedeCrear" @click="mostrarModal(null)" class="btn-primario">
        <i class="bi bi-plus-circle me-1"></i> Nueva Marca
      </button>
    </div>

    <div class="tarjeta">
      <div class="tarjeta-cuerpo">
        <div v-if="cargando" class="text-center py-5">
          <div class="spinner-border" role="status" style="color: var(--primary-main);">
            <span class="visually-hidden">Cargando...</span>
          </div>
        </div>

        <div v-else-if="marcas.length === 0" class="alerta-info">
          No se encontraron marcas registradas.
        </div>

        <div v-else class="table-responsive">
          <table class="tabla-datos">
            <thead>
              <tr>
                <th>ID</th>
                <th>Marca</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="marca in marcas" :key="marca.id">
                <td>{{ marca.id }}</td>
                <td><strong>{{ marca.marca }}</strong></td>
                <td>
                  <div class="btn-group btn-group-sm">
                    <button v-if="puedeEditar" @click="mostrarModal(marca)" class="btn-accion btn-accion-editar" title="Editar">
                      <i class="bi bi-pencil"></i>
                    </button>
                    <button v-if="puedeEliminar" @click="confirmarEliminar(marca)" class="btn-accion btn-accion-eliminar" title="Eliminar">
                      <i class="bi bi-trash"></i>
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- Modal Crear/Editar -->
    <div v-if="modalVisible" class="modal-fondo">
      <div class="modal-contenido">
        <div class="modal-encabezado">
          <h5 class="modal-titulo">{{ marcaEnEdicion ? 'Editar' : 'Nueva' }} Marca</h5>
          <button type="button" class="btn-close" @click="cerrarModal"></button>
        </div>
        <div class="modal-cuerpo">
          <form @submit.prevent="guardarMarca">
            <div class="campo-grupo">
              <label class="campo-etiqueta">Nombre *</label>
              <input
                v-model="formulario.marca"
                type="text"
                class="campo-entrada"
                required
                placeholder="Nombre de la marca"
              />
            </div>
          </form>
        </div>
        <div class="modal-pie">
          <button type="button" class="btn-secundario" @click="cerrarModal">Cancelar</button>
          <button type="button" class="btn-primario" @click="guardarMarca" :disabled="guardando">
            <span v-if="guardando" class="spinner-border spinner-border-sm me-1"></span>
            {{ guardando ? 'Guardando...' : 'Guardar' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Modal Confirmar Eliminar -->
    <div v-if="marcaParaEliminar" class="modal-fondo">
      <div class="modal-contenido">
        <div class="modal-encabezado">
          <h5 class="modal-titulo">Confirmar Eliminación</h5>
          <button type="button" class="btn-close" @click="marcaParaEliminar = null"></button>
        </div>
        <div class="modal-cuerpo">
          <p>Desea eliminar la marca <strong>{{ marcaParaEliminar.marca }}</strong>?</p>
          <p class="texto-secundario small">Esta acción no se puede deshacer.</p>
        </div>
        <div class="modal-pie">
          <button type="button" class="btn-secundario" @click="marcaParaEliminar = null">Cancelar</button>
          <button type="button" class="btn-peligro" @click="eliminarMarca">Eliminar</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { inventarioService } from '@/services/inventarioService';
import notificationService from '@/services/notificationService';

export default {
  name: 'ClientInventarioMarcas',
  computed: {
    puedeCrear() {
      return this.$store.getters.puedoHacer('/inventario/marcas', 'crear')
    },
    puedeEditar() {
      return this.$store.getters.puedoHacer('/inventario/marcas', 'editar')
    },
    puedeEliminar() {
      return this.$store.getters.puedoHacer('/inventario/marcas', 'eliminar')
    }
  },
  data() {
    return {
      marcas: [],
      cargando: false,
      modalVisible: false,
      guardando: false,
      marcaEnEdicion: null,
      marcaParaEliminar: null,
      formulario: {
        marca: ''
      }
    };
  },
  mounted() {
    this.cargarMarcas();
  },
  methods: {
    async cargarMarcas() {
      this.cargando = true;
      try {
        const respuesta = await inventarioService.getMarcas();
        this.marcas = respuesta?.data || [];
      } catch (error) {
        console.error('Error al cargar marcas:', error);
        this.marcas = [];
      } finally {
        this.cargando = false;
      }
    },
    mostrarModal(marca) {
      if (marca) {
        this.marcaEnEdicion = marca;
        this.formulario = { marca: marca.marca };
      } else {
        this.marcaEnEdicion = null;
        this.formulario = { marca: '' };
      }
      this.modalVisible = true;
    },
    cerrarModal() {
      this.modalVisible = false;
      this.marcaEnEdicion = null;
      this.formulario = { marca: '' };
    },
    async guardarMarca() {
      if (!this.formulario.marca.trim()) {
        notificationService.warning('El nombre de la marca es requerido');
        return;
      }
      this.guardando = true;
      try {
        if (this.marcaEnEdicion) {
          await inventarioService.updateMarca(this.marcaEnEdicion.id, this.formulario);
          notificationService.success('Marca actualizada correctamente');
        } else {
          await inventarioService.createMarca(this.formulario);
          notificationService.success('Marca creada correctamente');
        }
        this.cerrarModal();
        this.cargarMarcas();
      } catch (error) {
        console.error('Error al guardar marca:', error);
        notificationService.error('Error al guardar la marca');
      } finally {
        this.guardando = false;
      }
    },
    confirmarEliminar(marca) {
      this.marcaParaEliminar = marca;
    },
    async eliminarMarca() {
      try {
        await inventarioService.deleteMarca(this.marcaParaEliminar.id);
        notificationService.success('Marca eliminada correctamente');
        this.marcaParaEliminar = null;
        this.cargarMarcas();
      } catch (error) {
        console.error('Error al eliminar marca:', error);
        notificationService.error('Error al eliminar la marca');
      }
    }
  }
};
</script>

<style scoped>
.marcas-contenedor {
  padding: 20px;
  color: var(--text-primary);
}
.tarjeta {
  background-color: var(--secondary-bg-main);
  border: 1px solid var(--border-main);
  border-radius: 8px;
  box-shadow: var(--shadow-sm);
}
.tarjeta-cuerpo { padding: 1rem; }
.tabla-datos { width: 100%; border-collapse: collapse; }
.tabla-datos th {
  background-color: var(--bg-main);
  color: var(--text-secondary);
  font-weight: 600;
  padding: 0.75rem;
  border-bottom: 1px solid var(--border-main);
  text-align: left;
}
.tabla-datos td {
  padding: 0.75rem;
  border-bottom: 1px solid var(--border-main);
  color: var(--text-primary);
}
.tabla-datos tr:hover td { background-color: var(--bg-main); }
.alerta-info {
  padding: 1rem;
  background-color: var(--bg-main);
  border: 1px solid var(--border-main);
  border-radius: 6px;
  color: var(--text-secondary);
}
.btn-primario {
  background-color: var(--primary-main);
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.9rem;
}
.btn-primario:hover { background-color: var(--primary-hover); }
.btn-primario:disabled { opacity: 0.6; cursor: not-allowed; }
.btn-secundario {
  background-color: var(--bg-main);
  color: var(--text-primary);
  border: 1px solid var(--border-main);
  padding: 0.5rem 1rem;
  border-radius: 6px;
  cursor: pointer;
}
.btn-peligro {
  background-color: var(--accent-danger);
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  cursor: pointer;
}
.btn-accion {
  border: none;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  cursor: pointer;
  margin: 0 2px;
}
.btn-accion-editar {
  background-color: var(--primary-main);
  color: white;
}
.btn-accion-eliminar {
  background-color: var(--accent-danger);
  color: white;
}
.btn-accion:hover { opacity: 0.85; }
.modal-fondo {
  position: fixed;
  inset: 0;
  background-color: rgba(0,0,0,0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1050;
}
.modal-contenido {
  background-color: var(--secondary-bg-main);
  border: 1px solid var(--border-main);
  border-radius: 8px;
  width: 100%;
  max-width: 500px;
  box-shadow: var(--shadow-md);
}
.modal-encabezado {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.25rem;
  border-bottom: 1px solid var(--border-main);
}
.modal-titulo { margin: 0; color: var(--text-primary); font-size: 1.1rem; }
.modal-cuerpo { padding: 1.25rem; }
.modal-pie {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  padding: 1rem 1.25rem;
  border-top: 1px solid var(--border-main);
}
.campo-grupo { margin-bottom: 1rem; }
.campo-etiqueta { display: block; margin-bottom: 0.4rem; color: var(--text-secondary); font-size: 0.9rem; }
.campo-entrada {
  width: 100%;
  padding: 0.5rem 0.75rem;
  background-color: var(--bg-main);
  border: 1px solid var(--border-main);
  border-radius: 6px;
  color: var(--text-primary);
  font-size: 0.95rem;
}
.campo-entrada:focus {
  outline: none;
  border-color: var(--border-focus);
  box-shadow: 0 0 0 2px rgba(14,165,233,0.15);
}
.texto-secundario { color: var(--text-secondary); }
</style>